import { useFormik } from 'formik';
import { AutopopulateAccommodationPlacesScenario, EventType, EventUserStatus } from '@graphql/generated-base';
import { useTrackImpactRadiusMutation } from '@graphql/mutations/impactRadius.graphql.generated';
import { useUpdateTravelSettingsMutation } from '@graphql/mutations/travelSettings.graphql.generated';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import * as Yup from 'yup';
import { getLocalStorage, getSessionStorage, useHistory, useIdentify, useSegmentGroupCallForEvent, useTranslation } from '@shared/core';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { mapCurrencyCodeToTravelCurrencyCode } from '@shared/utils/currency/currencyMapper';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { calculateStayDates, getLocaleDateString } from '@shared/utils/dateHelpers';
import { useEffect, useMemo, useState } from 'react';
import type { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { addMonths } from 'date-fns';
import { useDesignParams } from '@apps/acquisitionFunnels/createWedding/hooks/useDesignParams';
import { getTelemetryDateLabel } from '@apps/acquisitionFunnels/createWedding/utils';
import { useFieldErrors } from '@shared/hooks/useFieldErrors';
import { useCreateWeddingTelemetry } from '@apps/acquisitionFunnels/createWedding/CreateWedding.telemetry';
import { createAndAttachPixelImage } from '@apps/acquisitionFunnels/createWedding/components/PixelImage/PixelImage';
import { getTimezone } from '@apps/acquisitionFunnels/createWedding/utils/getTimezone';
import { CREATE_WEDDING_APP_NAME, CREATE_WEDDING_KEY, CREATE_WEDDING_NEW_INTENT_CONSUMED_KEY } from '@apps/acquisitionFunnels/createWedding/constants';
import { useEventState } from '@apps/acquisitionFunnels/createWedding/EventState';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { readAcquisitionContexts, readAndResetAcquisitionContexts } from '@shared/createEvent/acquisitionContextTracking';
import type { StoredAcquisitionContextDetails } from '@shared/createEvent/acquisitionContextTracking/acquisitionContextTracking.types';
import { addAction, addError } from '@shared/utils/logger';
import { getEventTypeConfig } from '@config';
import { getCreateEventPayload } from '@shared/utils/impactRadius/utilsImpactRadius';
import { useCreateEventFeatures, useDefaultTemplatePayload, useWebsiteSettingsFromQueryParam } from './hooks';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useDisclosure } from '@withjoy/joykit';
import { useAutoPopulation as useAccommodationsAutoPopulation } from '@shared/utils/accommodations';
import { useStayAreasAutoPopulation } from '@shared/utils/stayAreas/useStayAreasAutoPopulation';
import { runAutoPopulationTasks } from '@shared/utils/autoPopulation/runAutoPopulationTasks';
import { useEventPlaceActions } from '@shared/utils/eventPlace';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { getValidCoordinates } from '@shared/core/places';
import type { CreateEventMutation } from './schema.graphql.generated';
import type { UpdateTravelSettingsRequest, CreateEventPayload0301 } from '@graphql/generated-base';
import { recordCreateWeddingFlowEvent } from '@apps/acquisitionFunnels/createWedding/createWeddingFlowEvents';
import { useCreatePersonalizedMonogramContainer } from '@shared/utils/monogram/useCreatePersonalizedMonogramContainer';
import { useCreateWeddingApplyHeaderPresentationSettingsMutation } from './schema.graphql.generated';
import type { CreateEventFeature } from '@shared/constants/customPageMigration';
import { useWebsiteV2OnboardingVariant } from '@shared/hooks/useWebsiteV2OnboardingVariant';
import { useCanIntroduceWebsiteV2 } from '@shared/hooks/useCanIntroduceWebsiteV2';
import { useWebsiteV2Onboarding } from '@apps/acquisitionFunnels/createWedding/components/WebsiteV2Onboarding/WebsiteV2OnboardingContext';
import { useWebsiteV2ProvisionEventFlagMutation } from '@apps/acquisitionFunnels/createWedding/components/WebsiteV2Onboarding/websiteV2Onboarding.graphql.generated';

export const PENDING_CREATION_KEY = '@@createwedding/pending-creation';

/**
 * When the browser locale is generic English (en-US / en) but the venue is in
 * an English-speaking country outside the US, override the event locale to
 * match the venue country. This ensures currency/formatting defaults are
 * correct for couples whose browser doesn't reflect their actual country.
 */
const VENUE_COUNTRY_TO_ENGLISH_LOCALE: Readonly<Record<string, string>> = {
  AU: 'en-AU',
  CA: 'en-CA',
  NZ: 'en-NZ',
  GB: 'en-GB',
};

// Currency that pairs with each venue-derived locale, so travel/accommodations
// defaults stay consistent with the overridden event locale.
const VENUE_COUNTRY_TO_CURRENCY: Readonly<Record<string, CurrencyCodeType>> = {
  AU: 'AUD',
  CA: 'CAD',
  NZ: 'NZD',
  GB: 'GBP',
};

const GENERIC_ENGLISH_LOCALES = new Set(['en-US', 'en']);

export interface EventVenueLocation
  extends Readonly<{
    placeId?: string;
    locationValue?: string;
    displayName?: string;
    address?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
    countryCode?: string;
  }> {}

export type CreateWeddingFormFields = Readonly<{
  ownerFirstName: string;
  partnerFirstName: string | null;
  ownerLastName: string | null;
  weddingDate: Date | null;
  isDateValid: boolean;
  location: Location | null;
  eventVenue: EventVenueLocation | null;
  font?: string;
  template?: string;
  scenario?: string;
  theme?: string;
  locale: string;
}>;

export type OnEventCreateSuccessHandler = (data: NonNullable<CreateEventMutation['createEventV0301']>) => void;

export interface CreateWeddingFormProps
  extends Readonly<{
    skipWaitForDesignParamToSettle?: boolean;
    onAfterEventCreateInitiated?: () => void;
    // The success of the event is handled in different ways depending on the flow
    onEventCreateSuccess?: OnEventCreateSuccessHandler;
    isCreateCtaLoading?: boolean;
    logoutRedirectUri?: string;
    /**
     * Funnel-only: offer the WebsiteV2 beta-vs-classic choice after submit (see
     * useIsEligibleForWebsiteV2). Never set by the PublicShop reuse of this form.
     */
    enableWebsiteV2Onboarding?: boolean;
  }> {}

interface UseCreateWeddingFormControllerArgs extends CreateWeddingFormProps {}

export const useCreateWeddingFormController = (args: UseCreateWeddingFormControllerArgs) => {
  const { enableWebsiteV2Onboarding, logoutRedirectUri, onAfterEventCreateInitiated, onEventCreateSuccess, skipWaitForDesignParamToSettle } = args;
  const authProvider = useAuth();
  const { identify } = useIdentify();
  const { createEvent, addEventCreatedListener } = useEventState();
  const localStorage = getLocalStorage();
  const sessionStorage = getSessionStorage();
  const { tq } = useTranslation('createWedding');
  const { designParams, settled } = useDesignParams();
  const telemetry = useCreateWeddingTelemetry();
  // eventMemberships is only consumed inside addEventCreatedListener (after createEvent
  // resolves), so by the time it's read auth has settled and the query has run. Skipping
  // pre-hydration keeps GQL off the SSR/initial-render path.
  const { eventMemberships } = useEventMemberships(undefined, { skip: !authProvider.isLoggedIn });
  const [blockWeddingDate, setBlockWeddingDate] = useState<boolean>(false);
  const [blockEventVenue, setBlockEventVenue] = useState<boolean>(false);
  const [trackImpactRadius] = useTrackImpactRadiusMutation();
  const enableBlankDates = useFeatureValue('enableBlankDates');
  const { getWebsiteDesignSettings, getWebsiteHeaderLayout } = useWebsiteSettingsFromQueryParam();
  const applyDefaultTemplatePayload = useDefaultTemplatePayload();
  const queryParamHelper = useQueryParamHelper();
  const customEventQueryParam = queryParamHelper.getValueString('customEvent');
  const isNewEventIntent = queryParamHelper.getValueString('new') === 'true';
  const { isOpen: isCustomEventDialogOpen, onClose: onCloseCustomEventDialog, onOpen: onOpenCustomEventDialog } = useDisclosure();
  const [isCreateWeddingHeaderLayoutExperimentEnabled] = useIsFeatureEnabled('createWeddingHeaderLayoutExperiment');
  const [isApplyMonogramInitialsEnabled] = useIsFeatureEnabled('createWeddingApplyMonogramInitials');
  const { createMonogramFromJsonContainerId } = useCreatePersonalizedMonogramContainer();
  const [applyHeaderPresentationSettings] = useCreateWeddingApplyHeaderPresentationSettingsMutation();
  const history = useHistory();

  const { variant: websiteV2Variant } = useWebsiteV2OnboardingVariant();
  // Audience gate shared with the dashboard introduction (`useCanIntroduceWebsiteV2`, super-admin
  // only today). This funnel choice is the only other path that flips `useWebsiteV2`, and the
  // provisioning mutation below carries no server-side superadmin check of its own, so this client
  // gate is the whole guard. It stays in place while `enableWebsiteV2Onboarding` is parked so that
  // re-enabling the funnel choice cannot quietly open the beta to every signup.
  const canIntroduceWebsiteV2 = useCanIntroduceWebsiteV2();
  const isWebsiteV2Eligible = websiteV2Variant !== 'off' && canIntroduceWebsiteV2.value && !canIntroduceWebsiteV2.isLoading;
  // undefined outside the funnel's FormFlowProviders (PublicShop reuse) — the choice never engages there.
  const websiteV2Onboarding = useWebsiteV2Onboarding();
  // Validated form values stashed while the beta-vs-classic choice overlay is up; null = closed.
  const [websiteV2ChoiceValues, setWebsiteV2ChoiceValues] = useState<CreateWeddingFormFields | null>(null);
  const [provisionWebsiteV2Flag] = useWebsiteV2ProvisionEventFlagMutation();

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();
  const [updateTravelSettings] = useUpdateTravelSettingsMutation();
  // Preserve full region-specific locale (e.g., es-ES, fr-FR, it-IT) for server.
  // i18n and date formatting systems will normalize these when interpreting.
  const { currency, languageCodeIso15897: eventLocale } = useGeoBasedLocale(undefined, { includeStoredLanguage: true });

  const { getEventFeatures } = useCreateEventFeatures();

  const { startAutoPopulation: startAccommodationsAutoPopulation } = useAccommodationsAutoPopulation({
    mode: 'start-only',
    context: 'createWedding',
  });

  const { startAutoPopulation: startStayAreasAutoPopulation } = useStayAreasAutoPopulation({
    mode: 'start-only',
    context: 'createWedding',
  });

  const { createEventPlace } = useEventPlaceActions({
    onCompleted: () => {},
    onError: () => {},
  });

  // Auto populate accommodations
  const autoPopulateAccommodations = useEventCallback(async (eventId: string, latitude?: number, longitude?: number, placeId?: string) => {
    await startAccommodationsAutoPopulation({
      venue: { latitude, longitude, googlePlaceId: placeId },
      eventId,
      scenario: AutopopulateAccommodationPlacesScenario.eventCreate,
    });
  });

  // Auto populate stay areas
  const autoPopulateStayAreas = useEventCallback(async (eventId: string, latitude?: number, longitude?: number) => {
    await startStayAreasAutoPopulation({
      venue: getValidCoordinates({ latitude, longitude }),
      eventId,
    });
  });

  const waitForDesignDeferred = useMemo(() => {
    let outsideResolve: (value?: unknown) => void = () => {};
    const promise = new Promise(resolve => {
      outsideResolve = resolve;
    });
    return { promise, resolve: outsideResolve };
  }, []);

  const shouldResolveDesignPromise = settled || skipWaitForDesignParamToSettle;
  const headerLayout = useMemo(
    () => (isCreateWeddingHeaderLayoutExperimentEnabled ? getWebsiteHeaderLayout() : null),
    [getWebsiteHeaderLayout, isCreateWeddingHeaderLayoutExperimentEnabled],
  );
  useEffect(() => {
    if (shouldResolveDesignPromise) {
      waitForDesignDeferred.resolve();
    }
  }, [shouldResolveDesignPromise, waitForDesignDeferred]);

  // Whether this load is the first arrival into the start-fresh flow this
  // session. On the very first `?new=true` entry we ignore any draft persisted
  // from a previous session; once consumed (below), a draft started during this
  // session is restored on refresh, preserving the JW-41 persistence behavior.
  // The `?new=true` param must stay in the URL (useAutoRedirectToScenario /
  // JW-53 depends on it), so we gate on a session flag instead of stripping it.
  const isFreshNewEventEntry = isNewEventIntent && !sessionStorage.getItem(CREATE_WEDDING_NEW_INTENT_CONSUMED_KEY);

  const initialValuesFromLocalStorage = useMemo(() => {
    if (isFreshNewEventEntry) {
      return;
    }

    const values = localStorage.getItem(CREATE_WEDDING_KEY);

    if (!values || values === 'undefined') {
      return;
    }

    try {
      const parsedVal = JSON.parse(values);
      return { ...parsedVal, ...(parsedVal.weddingDate ? { weddingDate: new Date(parsedVal.weddingDate) } : {}) };
    } catch {
      localStorage.removeItem(CREATE_WEDDING_KEY);
      return;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Consume the start-fresh intent once per session: drop the stale draft from a
  // previous session and mark the intent handled so a later refresh keeps the
  // in-progress draft. Side effect lives in an effect (not the useMemo above) so
  // render stays pure.
  useEffect(() => {
    if (isFreshNewEventEntry) {
      localStorage.removeItem(CREATE_WEDDING_KEY);
      sessionStorage.setItem(CREATE_WEDDING_NEW_INTENT_CONSUMED_KEY, 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrackImpactRadius = async (event: CreateEventMutation) => {
    try {
      const eventTypeConfig = getEventTypeConfig(EventType.wedding);
      const payload = getCreateEventPayload(
        eventTypeConfig.impactRadiusCampaignId,
        eventTypeConfig.impactRadiusCreateEventActionTrackerId,
        event.createEventV0301?.id,
        authProvider.currentUser.profile?.activeAlias?.auth0Id,
      );

      if (!payload) {
        return;
      }

      await trackImpactRadius({ variables: { payload } });
    } catch (e) {
      telemetry.trackError('CreateWeddingTrackImpactRadius', e);
    }
  };

  const handleCreateEvent = async (values: CreateWeddingFormFields, options?: Readonly<{ extraEventFeatures?: ReadonlyArray<CreateEventFeature> }>) => {
    const venueCoordinates = !blockEventVenue ? { lat: values.eventVenue?.latitude, lng: values.eventVenue?.longitude } : undefined;
    const { timezoneId, timezoneMethod } = await getTimezone(venueCoordinates);
    const eventDate = blockWeddingDate ? null : values.weddingDate;
    const eventDateToIsoString = eventDate?.toISOString() || null;
    const durationDays = eventDate ? Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : undefined;

    telemetry.dateSet(eventDateToIsoString ?? 'dateUndecided', getTelemetryDateLabel(eventDate), durationDays);
    telemetry.timezoneRegistered({ timezoneId, timezoneMethod });
    telemetry.submitDetailsClicked(authProvider.currentUser?.profile?.email ?? '');
    recordCreateWeddingFlowEvent({
      action: 'FormSubmitted',
      venueProvided: !blockEventVenue && !!values.eventVenue?.placeId,
      dateProvided: !blockWeddingDate && !!eventDate,
      lastNameProvided: !!values.ownerLastName,
      durationDays: durationDays ?? null,
    });
    const acquisitionContext = readAcquisitionContexts();
    let forceHotelBlockEnabled = false;
    const checkAcquisitionContextData = (dataDetails: StoredAcquisitionContextDetails, key: string, value: string) => {
      for (const item of dataDetails.data) {
        if (item.key === key && item.value === value) {
          return true;
        }
      }
      return false;
    };

    acquisitionContext.forEach(item => {
      if (item?.relativeUrl?.includes('hotel-room-blocks') || checkAcquisitionContextData(item, 'utm_campaign', '[SAGE]-Wedding-Website-New-MarComm-Travel-Segments-Only')) {
        forceHotelBlockEnabled = true;
      }
    });

    addAction('checkForceHotelBlockEnabled', { acquisitionContext, forceHotelBlockEnabled, userId: authProvider.currentUser?.profile?.id });

    await waitForDesignDeferred.promise;

    const eventLocation = blockEventVenue
      ? {}
      : {
          venue: {
            lat: values.location?.lat,
            lng: values.location?.lng,
            placeId: values.location?.placeId,
            placeName: values.eventVenue?.displayName ?? values.location?.place,
            countryCode: values.eventVenue?.countryCode ?? null,
          },
        };
    // If the browser locale is generic English but the venue is in an
    // English-speaking country, use the venue country's locale (and matching
    // currency) instead. Skipped when the venue is omitted ("still deciding"),
    // so a stale countryCode from a previously-selected venue can't leak through.
    const venueCountryCode = blockEventVenue ? undefined : values.eventVenue?.countryCode;
    const venueLocaleOverride = venueCountryCode && GENERIC_ENGLISH_LOCALES.has(values.locale) ? VENUE_COUNTRY_TO_ENGLISH_LOCALE[venueCountryCode] : undefined;
    // Keep travel/accommodations currency in sync with the overridden locale.
    const travelCurrency = venueLocaleOverride && venueCountryCode ? VENUE_COUNTRY_TO_CURRENCY[venueCountryCode] : currency;

    let payload: CreateEventPayload0301 = {
      eventType: EventType.wedding,
      ownerFirstName: values.ownerFirstName,
      fianceeFirstName: values.partnerFirstName,
      ownerLastName: values.ownerLastName,
      fianceeLastName: '',
      font: values.font,
      template: values.template,
      mainTzid: timezoneId,
      theme: values.theme,
      mainDate: enableBlankDates ? eventDateToIsoString : eventDateToIsoString || addMonths(new Date(), 9).toISOString(),
      acquisitionContext: {
        currentTimestamp: Date.now(),
        details: readAndResetAcquisitionContexts(),
      },
      eventFeatures: [...getEventFeatures(), ...(options?.extraEventFeatures ?? [])],
      locale: venueLocaleOverride ?? values.locale,
      ...eventLocation,
    };

    const websiteDesignSettings = getWebsiteDesignSettings();

    if (websiteDesignSettings) {
      payload.websiteDesignSettings = websiteDesignSettings;
    }

    if (isCreateWeddingHeaderLayoutExperimentEnabled && headerLayout) {
      payload.headerLayout = headerLayout;
    }

    if (!websiteDesignSettings && !headerLayout) {
      const { payload: defaultTemplatePayload, error } = await applyDefaultTemplatePayload(payload);
      if (error) {
        formik.setSubmitting(false);
        return;
      }
      payload = defaultTemplatePayload;
    }
    // Capture the header layout actually applied to the event — it may come from the query-param path
    // (local headerLayout) or the default-theme experiment payload — so monogram personalization below
    // works regardless of which path delivered it.
    const effectiveHeaderLayout = payload.headerLayout;
    createEvent({
      variables: {
        payload,
      },
    });
    createAndAttachPixelImage({ action: 'lead' });
    addEventCreatedListener(async event => {
      if (event.createEventV0301) {
        localStorage.removeItem(CREATE_WEDDING_KEY);
        const { id: eventId, firebaseId, website, info } = event.createEventV0301!;

        executeSegmentGroupCallForEvent(firebaseId, {
          postgresEventId: eventId,
          eventType: info.eventType,
        });
        const adminOrOwnerEventCount = eventMemberships?.filter(x => x.status === EventUserStatus.admin || x.status === EventUserStatus.owner).length;
        telemetry.weddingCreated({
          website,
          eventId: firebaseId,
          email: authProvider.currentUser.profile?.email ?? '',
          adminOrOwnerEventCount,
          venueLocationProvided: !blockEventVenue && !!values.eventVenue?.placeId,
          venueLocationSkipped: blockEventVenue,
          lastNameProvided: !!values.ownerLastName,
        });
        recordCreateWeddingFlowEvent({
          action: 'EventCreated',
          eventId: firebaseId,
          website,
          adminOrOwnerEventCount,
          venueProvided: !blockEventVenue && !!values.eventVenue?.placeId,
          dateProvided: !blockWeddingDate && !!eventDate,
          lastNameProvided: !!values.ownerLastName,
          durationDays: durationDays ?? null,
        });
        handleTrackImpactRadius(event);

        if (isApplyMonogramInitialsEnabled) {
          // Run the independent post-creation enrichment concurrently. The TyingTheKnot loading gate
          // waits for this listener to settle before its hard-navigation redirect (which would abort
          // any in-flight request), so we keep awaiting all of it — but as max() instead of sum().
          // The only ordering that must be preserved is createEventPlace -> autopopulation.
          await Promise.allSettled([
            // Travel settings (currency + stay dates).
            (async () => {
              try {
                const payload: UpdateTravelSettingsRequest = {
                  eventId,
                  currencyCode: mapCurrencyCodeToTravelCurrencyCode(travelCurrency),
                };

                if (eventDate) {
                  const stayDates = calculateStayDates(eventDate.toISOString());
                  payload.checkInDate = getLocaleDateString(stayDates.checkIn.date);
                  payload.checkOutDate = getLocaleDateString(stayDates.checkOut.date);
                }

                await updateTravelSettings({
                  variables: { payload },
                });
              } catch (error) {
                addError(error, { context: 'CreateWedding/updateTravelSettings', eventId, currency: travelCurrency });
              }
            })(),

            // Personalize the monogram header with the couple's initials. The header is attached at
            // creation only as a reference to the shared template container, so we clone it into a
            // per-event container with the initials applied and re-point the header at it.
            (async () => {
              const monogramTemplateContainerId: string | undefined = effectiveHeaderLayout?.dataJSON?.jsonContainerId;
              if (!effectiveHeaderLayout || !monogramTemplateContainerId || !values.partnerFirstName) return;
              try {
                const jsonContainerId = await createMonogramFromJsonContainerId(monogramTemplateContainerId, {
                  eventId,
                  ownerFirstName: values.ownerFirstName,
                  fianceeFirstName: values.partnerFirstName,
                  eventDateMs: undefined, // initials only
                });

                if (jsonContainerId) {
                  await applyHeaderPresentationSettings({
                    variables: {
                      payload: {
                        eventId,
                        dataJSON: { ...effectiveHeaderLayout.dataJSON, jsonContainerId },
                        schemaURL: effectiveHeaderLayout.schemaURL,
                        websiteLayout: effectiveHeaderLayout.websiteLayout ?? '',
                      },
                    },
                  });
                }
              } catch (error) {
                addError(error, { context: 'CreateWedding/applyMonogramInitials', eventId });
              }
            })(),

            // Venue place, then venue-dependent autopopulation (must stay ordered).
            (async () => {
              if (blockEventVenue || !values.eventVenue?.placeId) return;
              await createEventPlace({
                payload: {
                  googlePlaceId: values.eventVenue.placeId,
                },
                eventId,
              });
              await runAutoPopulationTasks('CreateWedding', eventId, [
                {
                  operation: 'accommodations',
                  enabled: true,
                  run: () => autoPopulateAccommodations(eventId, values.eventVenue?.latitude, values.eventVenue?.longitude, values.eventVenue?.placeId),
                },
                {
                  operation: 'stayAreas',
                  enabled: true,
                  run: () => autoPopulateStayAreas(eventId, values.eventVenue?.latitude, values.eventVenue?.longitude),
                },
              ]);
            })(),
          ]);
        } else {
          try {
            const payload: UpdateTravelSettingsRequest = {
              eventId,
              currencyCode: mapCurrencyCodeToTravelCurrencyCode(travelCurrency),
            };

            if (eventDate) {
              const stayDates = calculateStayDates(eventDate.toISOString());
              payload.checkInDate = getLocaleDateString(stayDates.checkIn.date);
              payload.checkOutDate = getLocaleDateString(stayDates.checkOut.date);
            }

            await updateTravelSettings({
              variables: { payload },
            });
          } catch (error) {
            addError(error, { context: 'CreateWedding/updateTravelSettings', eventId, currency: travelCurrency });
          }

          if (!blockEventVenue && values.eventVenue?.placeId) {
            await createEventPlace({
              payload: {
                googlePlaceId: values.eventVenue.placeId,
              },
              eventId,
            });
            await runAutoPopulationTasks('CreateWedding', eventId, [
              {
                operation: 'accommodations',
                enabled: true,
                run: () => autoPopulateAccommodations(eventId, values.eventVenue?.latitude, values.eventVenue?.longitude, values.eventVenue?.placeId),
              },
              {
                operation: 'stayAreas',
                enabled: true,
                run: () => autoPopulateStayAreas(eventId, values.eventVenue?.latitude, values.eventVenue?.longitude),
              },
            ]);
          }
        }

        identify({ forceHotelBlockEnabled });

        onEventCreateSuccess?.(event.createEventV0301);
      }
    });

    onAfterEventCreateInitiated?.();

    if (!onEventCreateSuccess) {
      history.push(`/${CREATE_WEDDING_APP_NAME}/tying-the-knot`);
    }
  };

  const formik = useFormik<CreateWeddingFormFields>({
    initialValues: {
      ownerFirstName: '',
      ownerLastName: '',
      partnerFirstName: '',
      weddingDate: addMonths(new Date(), 9),
      location: null,
      eventVenue: null,
      ...initialValuesFromLocalStorage,
      ...designParams,
      isDateValid: true,
      locale: eventLocale,
    },
    validationSchema: Yup.object<CreateWeddingFormFields>({
      ownerFirstName: Yup.string().required(tq`We need your first name.`),
      ownerLastName: Yup.string().nullable(),
      partnerFirstName: Yup.string().nullable(),
      weddingDate: Yup.date()
        .nullable()
        .test('valid date', tq`Please re-enter the date in MM/DD/YYYY format`, function () {
          const isDateValid = this.parent.isDateValid;
          return blockWeddingDate || isDateValid;
        }),
      isDateValid: Yup.boolean(),
      location: (
        Yup.object<Location>({
          place: Yup.string(),
          lng: Yup.number(),
          lat: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<Location>
      ).nullable(true),

      eventVenue: (
        Yup.object<EventVenueLocation>({
          locationValue: Yup.string().when([], {
            is: () => blockEventVenue,
            then: Yup.string().notRequired(),
            otherwise: Yup.string().required(tq`You can add a venue you’re considering and update this later — or check the box to skip for now.`),
          }),
          latitude: Yup.number(),
          longitude: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<EventVenueLocation>
      )
        .nullable(true)
        .when([], {
          is: () => !blockEventVenue,
          then: (schema: Yup.ObjectSchema<EventVenueLocation>) =>
            schema.required(tq`You can add a venue you’re considering and update this later — or check the box to skip for now.`),
          otherwise: (schema: Yup.ObjectSchema<EventVenueLocation>) => schema.notRequired(),
        }),
      font: Yup.string(),
      template: Yup.string(),
      scenario: Yup.string(),
      theme: Yup.string(),
      locale: Yup.string().required(),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, formikHelpers) => {
      formikHelpers.setFieldTouched('eventVenue', true, false);
      // The website choice must precede the mutation: choosing beta provisions `useWebsiteV2`
      // on the create payload itself.
      if (enableWebsiteV2Onboarding && isWebsiteV2Eligible && websiteV2Onboarding) {
        recordCreateWeddingFlowEvent({ action: 'WebsiteV2ChoiceShown', websiteV2Variant });
        setWebsiteV2ChoiceValues(values);
        formikHelpers.setSubmitting(false);
        return;
      }
      handleCreateEvent(values);
    },
  });

  const runWebsiteV2BetaPath = (values: CreateWeddingFormFields) => {
    // Belt and braces for the superadmin gate above: never provision the flag for anyone else.
    if (!authProvider.isSuperAdmin) {
      handleCreateEvent(values);
      return;
    }
    websiteV2Onboarding?.setBetaChosen(true);
    // The eventFeatures path below is filtered by the BE allowlist, which does NOT admit
    // `useWebsiteV2` (verified 2026-09-03) — this listener is the working provisioning path
    // until event_service allowlists it. Runs before the redirect (TyingTheKnot waits for
    // listeners to settle), so the fresh admin boot reads the flag from BasicEventInfo.
    addEventCreatedListener(async event => {
      const created = event.createEventV0301;
      if (!created) return;
      try {
        await provisionWebsiteV2Flag({ variables: { eventId: created.id, flag: 'useWebsiteV2' } });
      } catch (error) {
        addError(error, { context: 'CreateWedding/provisionWebsiteV2Flag', eventId: created.id });
      }
    });
    handleCreateEvent(values, { extraEventFeatures: ['useWebsiteV2'] });
  };

  const websiteV2Choice = {
    isOpen: websiteV2ChoiceValues != null,
    variant: websiteV2Variant,
    chooseBeta: () => {
      if (!websiteV2ChoiceValues) return;
      recordCreateWeddingFlowEvent({ action: 'WebsiteV2BetaChosen', websiteV2Variant });
      runWebsiteV2BetaPath(websiteV2ChoiceValues);
    },
    // Skip = no decision; the beta is the default. Recorded separately from an explicit pick.
    skip: () => {
      if (!websiteV2ChoiceValues) return;
      recordCreateWeddingFlowEvent({ action: 'WebsiteV2ChoiceSkipped', websiteV2Variant });
      runWebsiteV2BetaPath(websiteV2ChoiceValues);
    },
    chooseClassic: () => {
      if (!websiteV2ChoiceValues) return;
      recordCreateWeddingFlowEvent({ action: 'WebsiteV2ClassicChosen', websiteV2Variant });
      websiteV2Onboarding?.setBetaChosen(false);
      handleCreateEvent(websiteV2ChoiceValues);
    },
    dismiss: () => setWebsiteV2ChoiceValues(null),
  };

  useEffect(() => {
    if (customEventQueryParam?.length) {
      onOpenCustomEventDialog();
      formik.setFieldValue('ownerFirstName', customEventQueryParam || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customEventQueryParam, onOpenCustomEventDialog]);

  const getFieldErrorProps = useFieldErrors<CreateWeddingFormFields>(formik);

  useEffect(() => {
    localStorage.setItem(CREATE_WEDDING_KEY, JSON.stringify(formik.values));
  }, [formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = () => authProvider.provideLogout({}, logoutRedirectUri ?? `${window.location.origin}/${CREATE_WEDDING_APP_NAME}`);

  return {
    formik,
    user: authProvider.currentUser.profile,
    isLoggedIn: authProvider.isLoggedIn,
    getFieldErrorProps,
    signOut,
    blockWeddingDate,
    setBlockWeddingDate,
    blockEventVenue,
    setBlockEventVenue,
    telemetry,
    isCustomEventDialogOpen,
    onCloseCustomEventDialog,
    websiteV2Choice,
  };
};
